import React, { useEffect, useRef } from 'react';
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  TextBold20Regular,
  TextItalic20Regular,
  TextUnderline20Regular,
  TextBulletList20Regular,
  Link20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
  toolbar: {
    display: 'flex',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  toolbarButton: {
    minWidth: 'auto',
    width: '32px',
    height: '32px',
    ...shorthands.padding('0'),
  },
  editorArea: {
    flex: 1,
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    overflowY: 'auto',
    fontSize: '14px',
    lineHeight: '1.6',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
});

interface RichTextEditorProps {
  /** HTML string. Set imperatively on the editor when it changes externally. */
  value: string;
  /** Fired with the editor's innerHTML on every edit. */
  onChange: (html: string) => void;
  /** Optional min-height for the editable surface. */
  minHeight?: number | string;
  className?: string;
}

/**
 * Shared rich-text editor (#259) — formatting toolbar + contentEditable surface.
 * Stores innerHTML; the backend sanitizes against an allowlist before sending.
 * Used by the Send-Email dialog and the email-template editor so both stay
 * byte-for-byte identical.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  minHeight,
  className,
}) => {
  const styles = useStyles();
  const editorRef = useRef<HTMLDivElement>(null);

  // Push external value changes (e.g. selecting a template) into the DOM, but
  // never while the user is mid-edit — when typing, `value` already equals the
  // editor's innerHTML, so the guard skips and the caret is preserved.
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const sync = () => onChange(editorRef.current?.innerHTML || '');

  const exec = (command: string, val?: string) => {
    editorRef.current?.focus();
    window.document.execCommand(command, false, val);
    sync();
  };

  const handleLink = () => {
    const url = window.prompt('Link URL');
    if (url) exec('createLink', url);
  };

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Button appearance="subtle" className={styles.toolbarButton} aria-label="Bold"
          icon={<TextBold20Regular />}
          onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} />
        <Button appearance="subtle" className={styles.toolbarButton} aria-label="Italic"
          icon={<TextItalic20Regular />}
          onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} />
        <Button appearance="subtle" className={styles.toolbarButton} aria-label="Underline"
          icon={<TextUnderline20Regular />}
          onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} />
        <Button appearance="subtle" className={styles.toolbarButton} aria-label="Bulleted list"
          icon={<TextBulletList20Regular />}
          onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} />
        <Button appearance="subtle" className={styles.toolbarButton} aria-label="Insert link"
          icon={<Link20Regular />}
          onMouseDown={(e) => { e.preventDefault(); handleLink(); }} />
      </div>
      <div
        ref={editorRef}
        className={className ? `${styles.editorArea} ${className}` : styles.editorArea}
        style={minHeight ? { minHeight } : undefined}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
      />
    </div>
  );
};

export default RichTextEditor;
